import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';
import { isAndroid, isNative } from './is-native';
import { locationIfAllowed } from './geolocation';

/**
 * JS bridge for the native `MuckaLocationPlugin`
 * (ios/App/App/MuckaLocationPlugin.swift +
 *  android/app/src/main/java/ai/mucka/MuckaLocationPlugin.kt).
 *
 * On native devices the plugin wraps Core Location (iOS) /
 * FusedLocationProviderClient inside a foreground service (Android).
 * Each location update is dispatched as a `locationUpdate` event;
 * permission changes as `permissionChanged`.
 *
 * The `startTrackingAndForward()` helper at the bottom is the high-level
 * entry point — it wires the listener to POST `/api/journey/location`
 * and `/api/journey/permission` so callers don't have to.
 *
 * 🚨 Background fixes are posted by the NATIVE code, not by the listener.
 * The WebView is suspended whenever the app is off screen, so a listener
 * here never ran for a fix taken in a pocket: production held 9 location
 * rows over 15 days on 23 Oct 2026, all `foreground`, none from the
 * background, from five people who had granted "Always". Binaries that know
 * `configure()` take a location token and post themselves; this listener
 * only posts for an older binary that does not, which is no worse than
 * before. And tracking used to start only from the switch-on sheet, so the
 * first relaunch ended it — `resumeTrackingOnLaunch()` restarts it.
 *
 * On web, every method is a graceful no-op so consumers can call them
 * unconditionally.
 */

export type PermissionStatus =
  | 'not_determined'
  | 'denied'
  | 'authorized_when_in_use'
  | 'authorized_always'
  | 'restricted';

export interface LocationUpdate {
  latitude: number;
  longitude: number;
  /** Horizontal accuracy in metres. */
  accuracy: number;
  /** Unix epoch in milliseconds. */
  timestamp: number;
}

export interface MuckaLocationStatus {
  permission: PermissionStatus;
  tracking: boolean;
}

interface MuckaLocationPlugin {
  /**
   * Hand the native code what it needs to post a fix by itself: a location
   * token (`/api/journey/native-token`) and the URL to post to. Null clears
   * both. Binaries built before this existed reject with "not implemented".
   */
  configure(options: { token: string | null; endpoint: string | null }): Promise<{ nativeDelivery: boolean }>;
  start(): Promise<void>;
  stop(): Promise<void>;
  requestAlwaysPermission(): Promise<{ status: PermissionStatus }>;
  requestNotificationPermission(): Promise<{ granted: boolean }>;
  getStatus(): Promise<MuckaLocationStatus>;
  openAppSettings(): Promise<void>;
  addListener(
    eventName: 'locationUpdate',
    listenerFunc: (data: LocationUpdate) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'permissionChanged',
    listenerFunc: (data: { status: PermissionStatus }) => void,
  ): Promise<PluginListenerHandle>;
  removeAllListeners(): Promise<void>;
}

const MuckaLocation = registerPlugin<MuckaLocationPlugin>('MuckaLocation');

/**
 * Track active listener handles so `stopTracking` can detach cleanly
 * and we don't leak handlers if the user toggles enable/disable several
 * times.
 */
let activeListeners: PluginListenerHandle[] = [];

const IN_BACKGROUND = 'in_background';

/**
 * Trigger the native location-permission prompt (iOS or Android).
 * On web returns `{ status: 'denied' }` so callers can branch sensibly.
 */
export async function requestAlwaysPermission(): Promise<{ status: PermissionStatus }> {
  if (!isNative()) return { status: 'denied' };
  return MuckaLocation.requestAlwaysPermission();
}

/**
 * Cross-platform notification-permission request used by the rationale
 * bottom sheet. iOS goes through `@capacitor-firebase/messaging` (which
 * works fine there — the NPE bug is Android-only). Android goes through
 * our own `MuckaLocationPlugin.requestNotificationPermission` to dodge
 * that NPE. Web is a no-op returning `{ granted: false }`.
 *
 * Always resolves — never throws — so the calling sheet can branch on
 * the outcome without try/catch noise. Idempotent on both platforms:
 * subsequent calls after a grant resolve immediately as `granted: true`,
 * and after a permanent deny they resolve as `granted: false` without
 * re-surfacing UI (the OS suppresses the dialog).
 */
export async function requestNotificationPermission(): Promise<{ granted: boolean }> {
  if (!isNative()) return { granted: false };
  const p = (await import('@capacitor/core')).Capacitor.getPlatform();
  if (p === 'ios') {
    try {
      const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
      const result = await FirebaseMessaging.requestPermissions();
      return { granted: result.receive === 'granted' };
    } catch {
      return { granted: false };
    }
  }
  try {
    return await MuckaLocation.requestNotificationPermission();
  } catch {
    return { granted: false };
  }
}

/**
 * Deep-link the user into this app's settings screen so they can flip
 * location permission back on after denying it. Works on both iOS
 * (UIApplication.openSettingsURLString) and Android
 * (Settings.ACTION_APPLICATION_DETAILS_SETTINGS). No-op on web.
 */
export async function openAppSettings(): Promise<void> {
  if (!isNative()) return;
  await MuckaLocation.openAppSettings();
}

/**
 * Current native location state. On web returns a fixed "denied"
 * placeholder.
 */
export async function getStatus(): Promise<MuckaLocationStatus> {
  if (!isNative()) return { permission: 'denied', tracking: false };
  return MuckaLocation.getStatus();
}

/**
 * Ask the server for a location token. `enabled: false` means the tracker is
 * off for this person — possibly switched off on another device — and the
 * phone should not be monitoring at all.
 */
async function fetchLocationToken(): Promise<{ enabled: boolean; token?: string } | null> {
  try {
    const res = await fetch('/api/journey/native-token', { method: 'POST', credentials: 'same-origin' });
    if (!res.ok) return null;
    return (await res.json()) as { enabled: boolean; token?: string };
  } catch {
    return null;
  }
}

/**
 * Whether Android will let us start the location service right now.
 *
 * 🚨 Android 12+ refuses to start a foreground service from the background,
 * and a plugin method that throws CRASHES the app (MUCKA-ANDROID-9: a 3.3.11
 * phone died in `MuckaLocationPlugin.start` with the app off screen). The boot
 * re-arms tracking after a token fetch and several bridge hops, plenty of time
 * for somebody to leave the app, and binaries already in the field cannot be
 * fixed, so the JS asks first. iOS has no such rule. A state we cannot read is
 * treated as off screen: the next resume tries again.
 */
async function mayStartLocationService(): Promise<boolean> {
  if (!isAndroid()) return true;
  try {
    return (await App.getState()).isActive;
  } catch {
    return false;
  }
}

/** True when the native code will post fixes itself. */
async function configureNativeDelivery(token: string | null): Promise<boolean> {
  try {
    const endpoint = token ? new URL('/api/journey/location', window.location.origin).href : null;
    const { nativeDelivery } = await MuckaLocation.configure({ token, endpoint });
    return nativeDelivery === true;
  } catch {
    // An older binary without configure(): the JS listener posts instead.
    return false;
  }
}

/**
 * Start significant-change monitoring on the device AND wire listeners
 * so each fix is forwarded to the backend. Idempotent — calling it
 * twice in a row tears down the previous listeners cleanly.
 *
 * Returns `{ ok: false, reason }` on web or when permission isn't
 * granted; the caller is expected to nudge the user up to permission
 * level first via `requestAlwaysPermission`.
 */
export async function startTrackingAndForward(
  prefetched?: { enabled: boolean; token?: string } | null,
): Promise<{ ok: boolean; reason?: string }> {
  if (!isNative()) return { ok: false, reason: 'not_native' };
  // Before the stop below too: stopping a running service and then being
  // refused the restart would end tracking until the next resume.
  if (!(await mayStartLocationService())) return { ok: false, reason: IN_BACKGROUND };

  await stopTracking({ keepNativeCredential: true }); // detach any stale listeners from a prior run

  const minted = prefetched ?? await fetchLocationToken();
  const nativeDelivery = minted?.token ? await configureNativeDelivery(minted.token) : false;

  const locationHandle = await MuckaLocation.addListener('locationUpdate', async (fix) => {
    // The native code already posted this one.
    if (nativeDelivery) return;
    try {
      await fetch('/api/journey/location', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          latitude: fix.latitude,
          longitude: fix.longitude,
          accuracy: fix.accuracy,
          timestamp: fix.timestamp,
          source: 'significant_change',
        }),
      });
    } catch (err) {
      // Failure here is non-fatal — the next fix will get its chance.
      console.warn('[mucka-location] failed to POST location:', err);
    }
  });
  activeListeners.push(locationHandle);

  const permissionHandle = await MuckaLocation.addListener('permissionChanged', async (evt) => {
    try {
      await fetch('/api/journey/permission', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: evt.status }),
      });
    } catch (err) {
      console.warn('[mucka-location] failed to POST permission:', err);
    }
  });
  activeListeners.push(permissionHandle);

  // Again, as late as possible: the token fetch above can take long enough
  // for them to have left the app.
  if (!(await mayStartLocationService())) {
    await stopTracking({ keepNativeCredential: true });
    return { ok: false, reason: IN_BACKGROUND };
  }

  try {
    await MuckaLocation.start();
    return { ok: true };
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    // A binary that catches the refusal itself rejects with the same reason;
    // the tracker is still on, so keep the credential for the retry.
    await stopTracking({ keepNativeCredential: reason === IN_BACKGROUND });
    return { ok: false, reason };
  }
}

/**
 * Put the phone back in step with the server when the app opens.
 *
 * Tracker on and permission granted → (re)start monitoring with a fresh
 * token. Tracker off → stop, in case this phone is still monitoring after
 * being switched off somewhere else. Anything we cannot read (offline, a
 * 401) leaves the phone as it is rather than guessing.
 */
export async function resumeTrackingOnLaunch(): Promise<'done' | 'deferred'> {
  if (!isNative()) return 'done';
  const minted = await fetchLocationToken();
  if (!minted) return 'done';
  trackerOnHere = minted.enabled;
  if (!minted.enabled) {
    const status = await getStatus().catch(() => null);
    if (status?.tracking) await stopTracking();
    else await configureNativeDelivery(null);
    return 'done';
  }
  const status = await getStatus().catch(() => null);
  if (status?.permission !== 'authorized_always' && status?.permission !== 'authorized_when_in_use') return 'done';
  const started = await startTrackingAndForward(minted);
  // Left the app before it could start: the caller should try on the next resume.
  return started.reason === IN_BACKGROUND ? 'deferred' : 'done';
}

/**
 * Stop monitoring and remove all listeners. Safe to call when not
 * tracking — the underlying plugin's stop() resolves on every state.
 */
export async function stopTracking(opts: { keepNativeCredential?: boolean } = {}): Promise<void> {
  if (!isNative()) return;
  for (const handle of activeListeners) {
    try { await handle.remove(); } catch { /* ignore */ }
  }
  activeListeners = [];
  try { await MuckaLocation.stop(); } catch { /* ignore */ }
  // Switched off: the phone must not keep a credential that posts locations.
  if (!opts.keepNativeCredential) await configureNativeDelivery(null);
}

export default MuckaLocation;

/** Set at launch from the server's answer; nothing pings unless the tracker is on. */
let trackerOnHere = false;
let lastPing = 0;
const PING_EVERY_MS = 2 * 60_000;

/**
 * Take a fresh position the moment somebody starts talking to Mucka.
 *
 * Significant-change monitoring reports only after the phone has moved about
 * 500m, so somebody sat at home or on site can have no recent fix at all, and
 * "what time do I need to leave?" then falls back to measuring from home. But
 * while they are talking to Mucka the app is on screen, so it can simply ask —
 * the same one-shot fix the tracker's own Refresh button takes, with no prompt
 * (`locationIfAllowed` returns nothing rather than asking).
 *
 * Fire-and-forget and throttled: called on opening voice and on every chat
 * send, it costs a cached fix at most once every two minutes. Posted as
 * `manual`, which the server keeps only inside working hours with the tracker
 * on, like a background fix — nobody pressed a button for this one.
 */
export function pingWhileTalkingToMucka(): void {
  if (!trackerOnHere || !isNative()) return;
  if (Date.now() - lastPing < PING_EVERY_MS) return;
  lastPing = Date.now();
  void (async () => {
    const here = await locationIfAllowed();
    if (!here) return;
    await fetch('/api/journey/location', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        latitude: here.lat,
        longitude: here.lng,
        accuracy: here.accuracy,
        timestamp: Date.now(),
        source: 'manual',
      }),
    });
  })().catch(() => {});
}
